import React from 'react';
import clsx from 'clsx';
import CurrencyTextField from 'core/components/CurrencyTextField';
import {
  isValidFeeAmount,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule';
import {
  FeeAmount,
  FeeScheduleLine,
  FeeScheduleProps,
} from 'pages/Dashboard/pages/Encounters/pages/VisitNote/components/PCC/FeeSchedule/types';

type FeeChargeCellProps = {
  feeLine: FeeScheduleLine;
  feeIndex: number;
  value: FeeScheduleLine[];
  onChange: FeeScheduleProps['onChange'];
  disabled: boolean;
  error: boolean;
};

function FeeChargeCell({
  feeLine,
  feeIndex,
  value,
  onChange,
  disabled,
  error,
}: FeeChargeCellProps) {
  function handleChange(chargeAmount?: FeeAmount) {
    onChange(value.map((currentFeeLine, currentIndex) => (
      currentIndex === feeIndex
        ? { ...currentFeeLine, chargeAmount }
        : currentFeeLine
    )));
  }

  return (
    <CurrencyTextField
      id={`configure-fee-schedule-${feeLine.serviceCode}-charge`}
      className="w-full [&_input]:!h-[34px] [&_input]:!rounded-lg [&_input]:!border-[#E2E8F0] [&_input]:!px-2.5 [&_input]:!text-[13px] [&_input]:!text-[#0F1B2D]"
      inputProps={{
        'aria-label': `Price for ${feeLine.serviceCode}`,
        inputMode: 'decimal',
      }}
      value={feeLine.chargeAmount}
      defaultValue={null}
      disabled={disabled}
      error={error}
      nullIfEmpty
      showErrorText={false}
      onChange={handleChange}
    />
  );
}

type Props = FeeScheduleProps & {
  ariaLabel: string;
};

export default function ConfigureFeeSchedule({
  value,
  onChange,
  disabled = false,
  error = false,
  ariaLabel,
}: Props) {
  return (
    <div
      className="overflow-hidden rounded-xl border border-[#E2E8F0]"
      role="table"
      aria-label={ariaLabel}
    >
      <div
        className="grid min-h-[34px] grid-cols-[76px_minmax(0,1fr)_96px] items-center gap-x-3 border-b border-[#E2E8F0] bg-[#F4F5F5] px-3.5 text-xs font-semibold text-[#475569]"
        role="row"
      >
        <div role="columnheader">Code</div>
        <div role="columnheader">Description</div>
        <div role="columnheader">Price</div>
      </div>
      {value.map((feeLine, feeIndex) => (
        <div
          key={feeLine.serviceCode}
          className={clsx(
            'grid min-h-11 grid-cols-[76px_minmax(0,1fr)_96px] items-center gap-x-3 px-3.5 py-1.5',
            feeIndex > 0 && 'border-t border-[#E2E8F0]',
          )}
          role="row"
        >
          <div role="cell">
            <span className="inline-block rounded-full border border-brand-200 bg-brand-100 px-2.5 py-0.5 text-xs font-bold text-brand-700">
              {feeLine.serviceCode}
            </span>
          </div>
          <div
            className="text-[13px] leading-[1.35] text-[#475569]"
            role="cell"
          >
            {feeLine.description}
          </div>
          <div role="cell">
            <FeeChargeCell
              feeLine={feeLine}
              feeIndex={feeIndex}
              value={value}
              onChange={onChange}
              disabled={disabled}
              error={error && !isValidFeeAmount(feeLine.chargeAmount)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
